import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { DatePicker, Field, Select } from '@/components/ui/Field'
import {
  toQuoteUpdatePayload,
  useUpdateQuote,
} from '@/features/quotes/api/useQuote'
import {
  draftFromRecord,
  type QuoteDraft,
} from '@/features/quotes/quoteDraft'
import { incotermOptions } from '@/lib/incoterms'
import { paymentOptions } from '@/lib/paymentOptions'
import type { QuoteDetail } from '@/types/domain'

/* The header fields answered ON the quote rather than on the way in.
 *
 * They did not disappear — every one is a column on `quotes` that prints on the
 * offer, and a field the form stops sending is a field the next whole-record PUT
 * silently empties. What changed is WHERE they are asked: each now sits on the
 * step that owns its subject, behind a disclosure, instead of on the screen a
 * salesperson meets first.
 *
 *   routing  pickup location · transit min–max · weight and volume overrides ·
 *            chargeable weight · final packing list
 *   pricing  Incoterm · demurrage · delivery terms · carrier line
 *
 * The payment option is the exception and is exported on its own: it is answered
 * on most quotes and the workbook prints it on the front of the sheet, so it
 * renders visible on step 3 rather than behind a press.
 *
 * ── Why a local draft and an explicit save ───────────────────────────────────
 *
 * The amend is a whole-record PUT (useUpdateQuote), because a partial body could
 * not distinguish "clear the delivery terms" from "leave them alone". A PUT per
 * keystroke would therefore rewrite the entire header on every character, and a
 * PUT on blur would fire while somebody was tabbing between two fields they had
 * not finished. So each surface holds its own draft and writes once when the
 * button is pressed.
 *
 * That is also why the draft is hydrated from the RECORD rather than shared with
 * step 1's: this surface edits a saved quote, and step 1's draft describes one
 * that did not exist yet.
 *
 * ── Why the draft is EDITS over the record rather than a copy of it ──────────
 *
 * The obvious shape — seed a whole QuoteDraft on mount and edit that — has a
 * quiet failure this screen would reach on its second save. Three of these
 * surfaces are on screen at once on step 3, and each one saves the WHOLE record.
 * A copy seeded on mount goes stale the moment a sibling saves: the payment
 * picker, seeded before the Incoterm was changed, would send the OLD Incoterm
 * and undo the edit the user just watched succeed.
 *
 * So what is held locally is only what THIS surface changed, and the payload is
 * rebuilt at save time from the record as it stands now with those edits laid
 * over it. Every field nobody touched comes from the freshest record there is.
 */

/** The record, plus whatever this surface has changed since it was opened. */
function useHeaderAmend(record: QuoteDetail) {
  const update = useUpdateQuote()
  const [edits, setEdits] = useState<Partial<QuoteDraft>>({})

  /* Rebuilt on every render rather than stored, which is the whole point: the
     record is the freshest copy the cache holds, and the edits are laid over it
     at the moment the payload is built. */
  const draft: QuoteDraft = { ...draftFromRecord(record), ...edits }

  return {
    draft,
    set: (patch: Partial<QuoteDraft>) =>
      setEdits((previous) => ({ ...previous, ...patch })),
    /* The edits are NOT cleared on success. They already equal the record by the
       time the refetch lands, and clearing them before it does would show the
       pre-save values for a frame — a field visibly reverting after a save that
       worked is the one thing this surface must never do. */
    save: () =>
      update.mutate({ id: record.id, payload: toQuoteUpdatePayload(record, draft) }),
    pending: update.isPending,
  }
}

/** The one control every disclosure ends with. Its own component so the two
 *  disclosures cannot drift into two different commit words. */
function SaveRow({
  onSave,
  pending,
  blockedReason,
}: {
  onSave: () => void
  pending: boolean
  blockedReason?: string
}) {
  const { t } = useTranslation()

  return (
    /* The span classes are the DISCLOSURES': they lay their fields out in a
       grid, and without them the button would share the last field's cell. The
       terms card stacks instead, where a span on a flex child is inert. */
    <div className="flex justify-end sm:col-span-2 lg:col-span-3">
      <Button
        variant="secondary"
        onClick={onSave}
        disabled={pending}
        blockedReason={blockedReason}
      >
        {pending ? t('common.form.saving') : t('common.actions.save')}
      </Button>
    </div>
  )
}

/* The terms as a READ-OUT, for every surface that may not edit them.
 *
 * Shared rather than written twice, because it is now needed in two places: the
 * deal terms on `TermsSection` and the practical three on `QuoteNotesCard`, each
 * of which falls back to it when the host does not ask the questions or the
 * server will not take the answers. Two copies would be two opinions about what
 * an unanswered term looks like, and that is the whole content of this thing.
 *
 * An em dash for an absent value, never a blank cell: "—" says the question was
 * asked and has no answer yet, where an empty cell reads as a rendering fault.
 * Falsy rather than nullish, so a stored empty string — which a header amend can
 * legitimately produce by clearing a field — reads as unanswered rather than as
 * a row that failed to render.
 */
export function TermsReadout({
  rows,
}: {
  rows: [label: string, value: string | undefined][]
}) {
  return (
    <dl className="flex flex-col">
      {rows.map(([label, value]) => (
        <div
          key={label}
          className="border-line-hair flex items-baseline gap-4 border-b py-2 last:border-b-0"
        >
          <dt className="text-text-muted w-32 shrink-0 text-[12.5px] font-light">
            {label}
          </dt>
          <dd className="text-text-body min-w-0 flex-1 text-[13px]">
            {value ? value : '—'}
          </dd>
        </div>
      ))}
    </dl>
  )
}

/* THE DEAL TERMS, as an editor rather than as a read-out (FR-11, FR-59).
 *
 * The three answers a client COMPARES between offers, in one card with one Save:
 * the Incoterm, how they pay, and the date after which none of it is a price any
 * more.
 *
 * The demurrage wording and the delivery description are NOT here, and are not
 * on this tab at all. They are prose about the deal rather than the deal, they
 * read as notes on the paper, and the notes card beside this one is a list of
 * sentences with no fields in it. They are answered on `QuoteFormSheet`, behind
 * the page header's Edit button, which asks every one of the header's fields.
 *
 * ── Why this exists beside the header sheet ─────────────────────────────────
 *
 * `QuoteFormSheet` asks these too, along with the rest of the header. The
 * Terms tab printed a list and offered no way to change a value, so a quote
 * whose Incoterm was agreed on the phone could only be corrected by reopening
 * that whole sheet — a form that also asks about the client, the dates and the
 * cargo owner, to fix one field the reader is already looking at.
 *
 * Same fields, same `useHeaderAmend`, same whole-record PUT as the sheet's save.
 * Neither re-implements the amend, which is the property that matters: the two
 * surfaces cannot drift into different opinions about what a term is.
 *
 * ── What it deliberately does NOT ask ───────────────────────────────────────
 *
 * The carrier display line. It names who is carrying the goods, not what was
 * agreed, and putting it under a heading that says "terms" is how it starts
 * being read as one. It is on the header sheet, with the routing measures,
 * which are absent here for the same reason: they belong beside the cargo they
 * override.
 *
 * Nothing here can be typed wrong, which is why there is no blockedReason on the
 * Save: two of the three are pickers and the third is a date control. The
 * numeric field that needed guarding left with the notes.
 */
export function DealTermsFields({ record }: { record: QuoteDetail }) {
  const { t } = useTranslation()
  const { draft, set, save, pending } = useHeaderAmend(record)

  return (
    /* ONE COLUMN, and it is not a preference. This card sits in the left half of
       the terms tab's `lg:grid-cols-2`, so it is about 320px wide on a 1440px
       screen — and Tailwind's `sm:` is a VIEWPORT query, not a container one. A
       two-column grid inside it therefore went two-up at exactly the width where
       each column is 150px, and the labels overlapped the fields under them.
       Stacking is what a narrow card can honestly do. */
    <div className="flex flex-col gap-4">
      {/* The eleven Incoterms® 2020 rules, not free text. The three letters ARE
          the meaning — CFR and CIF differ by who insures the cargo, DAP and DDP
          by who pays the duty — and a typo in them goes onto the offer as a term
          of the contract. Searchable, so "board" finds FOB. */}
      <Field label={t('quotes.form.incoterm')} hint={t('common.form.optional')}>
        {(props) => (
          <Select
            value={draft.incoterm}
            onChange={(incoterm) => set({ incoterm })}
            options={incotermOptions({
              current: draft.incoterm,
              seaOnlyNote: t('quotes.form.incotermSeaOnly'),
            })}
            placeholder={t('common.form.selectPlaceholder')}
            searchable
            {...props}
          />
        )}
      </Field>

      {/* A pick from the workbook's five rather than free text (FR-39): the same
          agreement used to arrive as "50/50", "50 / 50 payment" and "half up
          front", and nothing could group them. Always carries the stored value,
          so wording that predates the list still reads back and still prints. */}
      <Field label={t('quotes.form.paymentTerms')}>
        {(props) => (
          <Select
            value={draft.paymentTerms}
            onChange={(paymentTerms) => set({ paymentTerms })}
            options={paymentOptions({
              /* The option keys are derived from the workbook's own strings at
                 runtime, so they cannot be inferred from the catalogue; asserted
                 against one real key exactly as the role labels are, with
                 defaultValue covering wording the list does not know. */
              current: draft.paymentTerms,
              t: (key, fallback) =>
                t(key as 'quotes.paymentOptions.credit', fallback),
            })}
            placeholder={t('common.form.selectPlaceholder')}
            searchable
            {...props}
          />
        )}
      </Field>

      {/* FR-8's date, and the single most consequential field on the paper: past
          it, this offer is not a price. The composer prints it even when empty —
          as a dash — because its ABSENCE is itself the answer. */}
      <Field
        label={t('quotes.columns.validUntil')}
        hint={t('quotes.form.validUntilHint')}
      >
        {(props) => (
          <DatePicker
            value={draft.validUntil}
            onChange={(validUntil) => set({ validUntil })}
            {...props}
          />
        )}
      </Field>

      <SaveRow onSave={save} pending={pending} />
    </div>
  )
}

